import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  requestTicketMetric,
  getTicketMetricEvent,
  linkTicketMetricEvent,
  unlinkTicketMetricEvent,
  searchTicketMetricEvents,
  getTicketMetricAdmin,
  updateTicketMetricAdmin,
  testTicketMetricAdmin,
} from '../api';

export const ticketMetricKeys = {
  all: ['ticketmetric'],
  event: (eventId) => ['ticketmetric', 'event', String(eventId)],
  // sectionsKey: JSON string of our section names, so the key only changes
  // when the set of names does (not on every price write)
  view: (eventId, sectionsKey) => ['ticketmetric', 'event', String(eventId), sectionsKey],
  admin: ['ticketmetric', 'admin'],
};

// A pull is in progress (or waiting on TicketMetric's slow-down request)
export const TM_ACTIVE = new Set(['queued', 'running', 'paused']);

const POLL_MS = 3000;
const POLL_MAX_MS = 3 * 60 * 1000;   // give up after 3 min of continuous polling...
const PAUSE_GRACE_MS = 60 * 1000;    // ...or 1 min past the end of a TicketMetric pause
// Our names travel as ?sections=<json>. Cap the JSON so the URL-encoded
// request line stays well under nginx's 8 KB limit (the server takes 300).
const SECTIONS_JSON_MAX = 3000;
const SECTIONS_MAX = 300;

export const tmErrorText = (err, fallback = 'TicketMetric request failed') =>
  err?.response?.data?.error || err?.message || fallback;

function sectionsKeyOf(sections) {
  const out = [];
  let len = 2;
  for (const s of Array.isArray(sections) ? sections : []) {
    if (typeof s !== 'string' || !s.trim() || s.length > 100) continue;
    const add = JSON.stringify(s).length + 1;
    if (len + add > SECTIONS_JSON_MAX || out.length >= SECTIONS_MAX) break;
    out.push(s);
    len += add;
  }
  return JSON.stringify(out);
}

/**
 * TicketMetric sold data for one Skybox event (Compare panel).
 *
 * - On mount: exactly one POST /request (the ref guard absorbs StrictMode's
 *   double effect). The server reuses data younger than 10 min, otherwise it
 *   queues a pull and answers at once.
 * - Status: GET /events/:id, polled every 3 s only while a pull is queued /
 *   running / paused, for at most ~3 min (then `stalled`; `recheck()` resumes).
 *   Polling stops on unmount (react-query owns the timer).
 * - `sections` (our display names, memoized by the caller) are part of the
 *   query key: when they arrive the GET refetches with them — no new POST.
 * - Deliberately NOT tied to Compare's Refresh button, which fires after
 *   every price write.
 *
 * @returns {{ view, loading, error, requestError, stalled, recheck, retry, retrying,
 *             refresh, refreshing, link, linking, unlink, unlinking, search, searching }}
 */
export function useTicketMetric(eventId, { sections, enabled = true } = {}) {
  const queryClient = useQueryClient();
  const id = eventId ? String(eventId) : null;
  const on = !!id && enabled;
  const sectionsKey = useMemo(() => sectionsKeyOf(sections), [sections]);

  // ---- the one POST per Compare open ----
  const postedForRef = useRef(null);
  const [requestedFor, setRequestedFor] = useState(null);
  const [requestError, setRequestError] = useState(null);
  useEffect(() => {
    if (!on) return;
    // StrictMode mounts effects twice; refs survive that, so this POSTs once
    if (postedForRef.current === id) return;
    postedForRef.current = id;
    setRequestError(null);
    requestTicketMetric(id)
      .catch((err) => {
        if (postedForRef.current === id) setRequestError(tmErrorText(err));
      })
      .finally(() => {
        // Whatever the POST said, the GET (with our sections) is the source
        // of truth; it is enabled only now so it never races the enqueue
        queryClient.invalidateQueries({ queryKey: ticketMetricKeys.event(id) });
        if (postedForRef.current === id) setRequestedFor(id);
      });
  }, [on, id, queryClient]);

  // ---- status / data ----
  const [stalled, setStalled] = useState(false);
  const query = useQuery({
    queryKey: ticketMetricKeys.view(id, sectionsKey),
    queryFn: async () => (await getTicketMetricEvent(id, JSON.parse(sectionsKey))).data,
    enabled: on && requestedFor === id,
    staleTime: 10 * 1000,
    refetchOnWindowFocus: false,
    // Keep showing this event's numbers while the sections-key refetch runs
    // (never another event's)
    placeholderData: (prev, prevQuery) => (prevQuery?.queryKey?.[2] === id ? prev : undefined),
    refetchInterval: (q) => (!stalled && TM_ACTIVE.has(q.state.data?.status) ? POLL_MS : false),
  });

  const view = query.data || null;
  const active = TM_ACTIVE.has(view?.status);
  const pausedUntilMs = view?.status === 'paused' && view?.pausedUntil ? (Date.parse(view.pausedUntil) || 0) : 0;

  // Polling budget: 3 min of continuous polling, stretched to cover a
  // TicketMetric pause; restartPolling() (refresh / link / recheck) resets it
  const pollSinceRef = useRef(null);
  const [pollEpoch, setPollEpoch] = useState(0);
  useEffect(() => {
    if (!active) {
      pollSinceRef.current = null;
      setStalled(false);
      return undefined;
    }
    if (pollSinceRef.current == null) pollSinceRef.current = Date.now();
    const deadline = Math.max(pollSinceRef.current + POLL_MAX_MS, pausedUntilMs ? pausedUntilMs + PAUSE_GRACE_MS : 0);
    const wait = deadline - Date.now();
    if (wait <= 0) {
      setStalled(true);
      return undefined;
    }
    setStalled(false);
    const t = setTimeout(() => setStalled(true), wait);
    return () => clearTimeout(t);
  }, [active, pausedUntilMs, pollEpoch, id]);

  const restartPolling = useCallback(() => {
    pollSinceRef.current = null;
    setStalled(false);
    setPollEpoch((n) => n + 1);
  }, []);

  const afterChange = useCallback(() => {
    restartPolling();
    return queryClient.invalidateQueries({ queryKey: ticketMetricKeys.event(id) });
  }, [id, queryClient, restartPolling]);

  const { refetch } = query;
  const recheck = useCallback(() => {
    restartPolling();
    refetch();
  }, [restartPolling, refetch]);

  // ---- actions ----
  // Retry after the open-POST itself failed (e.g. a 429): same request, not forced
  const retryMutation = useMutation({
    mutationFn: async () => (await requestTicketMetric(id)).data,
    onSuccess: () => setRequestError(null),
    onSettled: afterChange,
  });
  // Refresh icon: force a new pull (server caps it at 6/min per user)
  const refreshMutation = useMutation({
    mutationFn: async () => (await requestTicketMetric(id, { force: true })).data,
    onSettled: afterChange,
  });
  // "Use this event" / pasted link: body { tmetricId } or { url }
  const linkMutation = useMutation({
    mutationFn: async (body) => (await linkTicketMetricEvent(id, body)).data,
    onSuccess: afterChange,
  });
  // "Wrong event?": unlink, never auto-pick that event again, search again
  const unlinkMutation = useMutation({
    mutationFn: async () => (await unlinkTicketMetricEvent(id)).data,
    onSuccess: afterChange,
  });
  // Picker search → { status: 'done', candidates } or { status: 'queued'|..., position };
  // the candidates are stored server-side, so the GET view lists them too
  const searchMutation = useMutation({
    mutationFn: async (q) => (await searchTicketMetricEvents(id, q)).data,
    onSuccess: afterChange,
  });

  return {
    view,
    loading: on && !view && !query.error && !requestError,
    error: query.error || null,
    requestError,
    stalled: active && stalled,
    recheck,
    retry: retryMutation.mutateAsync,
    retrying: retryMutation.isPending,
    refresh: refreshMutation.mutateAsync,
    refreshing: refreshMutation.isPending,
    link: linkMutation.mutateAsync,
    linking: linkMutation.isPending,
    unlink: unlinkMutation.mutateAsync,
    unlinking: unlinkMutation.isPending,
    search: searchMutation.mutateAsync,
    searching: searchMutation.isPending,
  };
}

// ---- Admin → TicketMetric (super admin only) ----------------------------------

export function useTicketMetricAdmin(options = {}) {
  return useQuery({
    queryKey: ticketMetricKeys.admin,
    queryFn: async () => (await getTicketMetricAdmin()).data,
    staleTime: 5 * 1000,
    refetchInterval: 10 * 1000, // live status while the tab is open (it unmounts when closed)
    ...options,
  });
}

export function useUpdateTicketMetricAdmin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (updates) => (await updateTicketMetricAdmin(updates)).data,
    onSuccess: (data) => {
      queryClient.setQueryData(ticketMetricKeys.admin, (old) => (old ? { ...old, config: data.config } : old));
      // A new login changes what every event view says (not_configured → ...)
      queryClient.invalidateQueries({ queryKey: ticketMetricKeys.all });
    },
  });
}

// 200 { status:'done', result } or 202 { status:'running'|'queued' } (the
// result then shows as lastTest on the admin GET)
export function useTestTicketMetricAdmin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => (await testTicketMetricAdmin()).data,
    onSettled: () => queryClient.invalidateQueries({ queryKey: ticketMetricKeys.admin }),
  });
}
